import { Button, Divider, Icon, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ReactNode, MouseEvent } from 'react';
import { LoadingButton } from '@mui/lab';
import { MaxModalFormWidth, ModalFormWidth } from '../../../constants/styles';
import { ConfirmDialog, IConfirmDialogProps } from './ConfirmDialog';

const ButtonsContainer = styled('div')`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  gap: 1rem;
`;

export enum ConfirmDialogTitleOptions {
  Warning = 'warning',
}

interface IConfirmDeletionDialogProps extends Pick<IConfirmDialogProps, 'open' | 'title'> {
  onConfirm: (e?: MouseEvent<HTMLElement>) => void;
  onClose: (e?: MouseEvent<HTMLElement>) => void;
  children: ReactNode | string;
  loading?: boolean;
  confirmLabel?: string;
  /** When true, the confirm button is disabled (e.g. until user selects a required option). */
  confirmDisabled?: boolean;
}

type IDeleteAndCancelButtons = Pick<
  IConfirmDeletionDialogProps,
  'onClose' | 'onConfirm' | 'loading' | 'confirmLabel' | 'confirmDisabled'
>;

const DeleteAndCancelButtons = ({
  onClose,
  onConfirm,
  loading,
  confirmLabel,
  confirmDisabled = false,
}: IDeleteAndCancelButtons) => (
  <Stack width='100%' gap='2rem'>
    <Divider />
    <ButtonsContainer>
      <Button variant='outlined' color='secondary' onClick={onClose} size='medium' disabled={loading}>
        Cancel
      </Button>
      <LoadingButton
        variant='contained'
        color='error'
        onClick={onConfirm}
        size='medium'
        loading={loading}
        disabled={loading || confirmDisabled}
        loadingPosition='start'
        startIcon={loading ? <Icon /> : <></>}
      >
        <span>{confirmLabel || 'Yes, Delete'}</span>
      </LoadingButton>
    </ButtonsContainer>
  </Stack>
);

export const ConfirmDeletionDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  children,
  loading,
  confirmLabel,
  confirmDisabled,
}: IConfirmDeletionDialogProps) => {
  return (
    <ConfirmDialog
      open={open}
      // Both exits close while the deletion runs. Nothing here can call it back — dismissing
      // would hide the dialog and let the delete land anyway, under a toast saying it succeeded.
      onClose={loading ? undefined : onClose}
      title={title}
      sx={{ '& .MuiDialog-paper': { width: ModalFormWidth, maxWidth: MaxModalFormWidth } }}
      type={ConfirmDialogTitleOptions.Warning}
      actionButtons={
        <DeleteAndCancelButtons
          onClose={onClose}
          onConfirm={onConfirm}
          loading={loading}
          confirmLabel={confirmLabel}
          confirmDisabled={confirmDisabled}
        />
      }
    >
      {children}
    </ConfirmDialog>
  );
};
